import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';
import { zhTW, enUS, TranslationKey, SupportedLanguage } from '../locales';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: TranslationKey;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Helper function to detect browser language
const detectBrowserLanguage = (): SupportedLanguage => {
  const browserLang = navigator.language || (navigator as any).userLanguage;

  // Check if browser language matches our supported languages
  if (browserLang.startsWith('zh')) {
    return 'zh-TW';
  } else if (browserLang.startsWith('en')) {
    return 'en-US';
  }

  // Default to Traditional Chinese
  return 'zh-TW';
};

// Get translations for a specific language
const getTranslations = (lang: SupportedLanguage): TranslationKey => {
  switch (lang) {
    case 'zh-TW':
      return zhTW;
    case 'en-US':
      return enUS;
    default:
      return zhTW;
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language from localStorage or browser detection
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const stored = localStorage.getItem('ultron-language');
    if (stored && (stored === 'zh-TW' || stored === 'en-US')) {
      return stored as SupportedLanguage;
    }
    return detectBrowserLanguage();
  });

  // Get current translations
  const t = getTranslations(language);

  // Update localStorage when language changes
  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    localStorage.setItem('ultron-language', lang);
    logger.log(`🌐 Language changed to: ${lang}`);
  };

  // Set HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Convenience hook for just getting translations
export const useTranslation = () => {
  const { t } = useLanguage();
  return { t };
};
